import { ref } from 'vue'
import type { BreakDTOV1 } from '@/agent/types/break'
import { defineStore } from 'pinia'
import { useActivitiesStore } from '@/agent/stores/activitiesStore'
import { useCtiStore } from '@/cti/stores/ctiStore'
import { CtiStatus } from '@/common/types/cti'
import { publish } from '@/common/services/eventBusService'
import { updateCurrentAgentActivity } from '@/common/services/AgentActivityStatusService'

export const useBreaksStore = defineStore('breaks', () => {
  const breaks = ref<BreakDTOV1[] | undefined>()
  const isOnBreak = ref(false)
  /** Mapped break code currently active in Everdesk (set when startBreak runs). */
  const currentCtiBreakCode = ref<string | null>(null)
  /** Last unmapped CTI break code received while not on break; restored when ending break from UI. */
  const unmappedCtiBreakCode = ref<string | null>(null)

  function clearCurrentCtiBreakCode() {
    currentCtiBreakCode.value = null
  }

  function isActiveMappedBreak(breakCode: string) {
    return isOnBreak.value && currentCtiBreakCode.value === breakCode
  }

  function updateUnmappedCtiBreakCode(code: string | null) {
    if (code === null) {
      unmappedCtiBreakCode.value = null
      return
    }

    if (!isOnBreak.value) {
      unmappedCtiBreakCode.value = code
    }
  }

  function startBreak(aBreak: BreakDTOV1, { notifyProvider = false }: { notifyProvider?: boolean } = {}) {
    const ctiStore = useCtiStore()

    currentCtiBreakCode.value = aBreak.ctiBreakCode
    isOnBreak.value = true

    if (ctiStore.status !== CtiStatus.ON_CALL) {
      ctiStore.status = CtiStatus.ON_BREAK
    }

    if (notifyProvider) {
      ctiStore.setAgentStatus('break', aBreak.ctiBreakCode)
    }
  }

  async function endBreak({ preserveActivity }: { preserveActivity: boolean }) {
    const activitiesStore = useActivitiesStore()
    const ctiStore = useCtiStore()

    publish('front:cti-stop-break')
    isOnBreak.value = false
    clearCurrentCtiBreakCode()

    const currentActivity = activitiesStore.userCurrentActivity
    const restoredCtiBreakCode = unmappedCtiBreakCode.value

    if (preserveActivity && currentActivity?.id && currentActivity.ctiBreakCode) {
      await updateCurrentAgentActivity(currentActivity.id)
      if (ctiStore.status !== CtiStatus.ON_CALL) {
        ctiStore.status = currentActivity.blockAutomaticPush ? CtiStatus.ON_BREAK : CtiStatus.AVAILABLE
      }
      ctiStore.setAgentStatus('break', currentActivity.ctiBreakCode)
    } else if (preserveActivity && restoredCtiBreakCode) {
      if (ctiStore.status !== CtiStatus.ON_CALL) {
        ctiStore.status = CtiStatus.ON_BREAK
      }
      ctiStore.setAgentStatus('break', restoredCtiBreakCode)
    } else if (preserveActivity) {
      // End-break button only: sync CTI when no activity to keep.
      // Not a plain else — CTI iframe "Available" passes preserveActivity: false and already sent no-break.
      ctiStore.setAgentStatus('no-break')
    }

    updateUnmappedCtiBreakCode(null)
  }

  return {
    breaks,
    isOnBreak,
    currentCtiBreakCode,
    unmappedCtiBreakCode,
    clearCurrentCtiBreakCode,
    isActiveMappedBreak,
    updateUnmappedCtiBreakCode,
    startBreak,
    endBreak,
  }
})
